import _ from "lodash";
import axios from "../lib/apiAxios";
import { toFormData } from "@/lib/uploadUtils";
import {
    ExperimentGroupTaskTemplate,
    SavedExperimentGroupTaskTemplate,
    SavedSchemaConfigTaskTemplate,
    SchemaConfigTaskTemplate,
} from "@/services/taskTemplates/taskTemplatesAPI";
import { ReadoutGroup, SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import { SavedTaskGroup, TaskGroup } from "@/services/taskGroups/taskGroupsAPI";

export function search(query: string) {
    if (_.isString(query) && query.trim().length > 2) {
        return get(`/experiment/_search?q=${encodeURIComponent(query)}`);
    } else {
        return [];
    }
}

export function buildExperimentAPI(protocolCode: string, experimentCode: string) {
    return {
        fetchThingsForSchema: async (thingSchemaId: string) => {
            return get(
                `/things/protocols/${protocolCode}/experiments/${experimentCode}/things/${thingSchemaId}`
            );
        },
        fetchThingSchemaIdsByThingIdMap: async () => {
            return get(
                `/things/protocols/${protocolCode}/experiments/${experimentCode}/thingSchemaIdsByThingId`
            );
        },
        fetchThingsForSchemas: async (thingSchemaIds: string) => {
            return get(`/things/protocols/${protocolCode}/experiments/${experimentCode}/things`, {
                params: { thingSchemaIds },
            });
        },
        searchThings: async (thingSchemaId: string, query: string) => {
            return get(
                `/things/protocols/${protocolCode}/experiments/${experimentCode}/thingSearch/${thingSchemaId}?q=${encodeURIComponent(query)}`
            );
        },
        updateExperiment(data) {
            return put(`/experiment/invaio/${experimentCode}`, data);
        },
    };
}

/**
 * @deprecated use thingAPI
 */
export const legacyExperimentAPI = {
    fetchExperimentByCode(experimentCode: string) {
        return get(`/experiment/${experimentCode}`);
    },
};

/**
 * Build a basic CRUD API object for the given base path
 */
export function buildConventionalCrudAPI<T, SavedT>(basePath: string) {
    async function fetchAll(): Promise<SavedT[]> {
        return get(`${basePath}`);
    }
    async function fetchById(id: string): Promise<SavedT> {
        return get(`${basePath}/${id}`);
    }
    async function create(data: T): Promise<SavedT> {
        return post(`${basePath}`, data);
    }
    async function update(data: T & { id: string }): Promise<SavedT> {
        return put(`${basePath}/${data.id}`, data);
    }
    async function doDelete(id: string): Promise<void> {
        return callDelete(`${basePath}/${id}`);
    }
    async function save(data: T | (T & { id: string })): Promise<SavedT> {
        if (data["id"]) {
            return await update(data as T & { id: string });
        } else {
            return await create(data);
        }
    }

    return {
        fetchAll,
        fetchById,
        create,
        update,
        delete: doDelete,
        save,
    };
}

export function buildTaskReferencesAPIForExperiment(experimentCode: string) {
    return buildConventionalCrudAPI(`/measurements/taskReferences/${experimentCode}`);
}

export function buildTaskThingDateEstimatesAPIForExperiment(experimentCode: string) {
    return buildConventionalCrudAPI(`/measurements/taskThingDateEstimates/${experimentCode}`);
}

export function buildExperimentReadoutGroupsAPI(experimentCode: string) {
    return buildConventionalCrudAPI(`/readoutGroups/byExperiment/${experimentCode}`);
}

export function buildProtocolReadoutGroupsAPI(protocolCode: string) {
    return buildConventionalCrudAPI(`/readoutGroups/byProtocol/${protocolCode}`);
}

export function buildSchemaConfigReadoutGroupsAPI(schemaConfigId: string) {
    return buildConventionalCrudAPI<ReadoutGroup, SavedReadoutGroup>(
        `/readoutGroups/bySchemaConfig/${schemaConfigId}`
    );
}

export function buildExperimentTaskGroupsAPI(experimentCode: string) {
    const basePath = `/measurements/experimentTaskGroups/${experimentCode}`;
    return buildTaskGroupsAPIForExperiment(basePath);
}

export function buildProtocolTaskGroupsAPI(protocolCode: string) {
    const basePath = `/measurements/protocolTaskGroups/${protocolCode}`;
    return buildTaskGroupsAPIForExperiment(basePath);
}

export function buildSchemaConfigTaskGroupsAPI(schemaConfigId: string) {
    const basePath = `/taskGroups/bySchemaConfig/${schemaConfigId}`;
    return buildTaskGroupsAPIForExperiment(basePath);
}

function buildTaskGroupsAPIForExperiment(basePath: string) {
    const baseCrudAPI = buildConventionalCrudAPI<TaskGroup, SavedTaskGroup>(basePath);

    return {
        ...baseCrudAPI,
        buildTaskGroupsFromTaskReferences: async () => {
            return post(`${basePath}/buildTaskGroupsFromTaskReferences`);
        },
        bulkSave: async (taskGroups: unknown[]) => {
            return post(`${basePath}/bulk`, taskGroups);
        },
    };
}

export function buildPhotosAPI(experimentCode: string) {
    const basePath = `/photos/${experimentCode}`;
    return buildConventionalCrudAPI(basePath);
}

export function buildProtocolTaskTemplatesAPI(protocolCode: string) {
    const basePath = `/taskTemplates/protocol/${protocolCode}`;
    return buildConventionalCrudAPI(basePath);
}

export function buildSchemaConfigTaskTemplatesAPI(schemaConfigId: string) {
    const basePath = `/taskTemplates/schemaConfig/${schemaConfigId}`;
    return buildConventionalCrudAPI<SchemaConfigTaskTemplate, SavedSchemaConfigTaskTemplate>(
        basePath
    );
}

export function buildExperimentGroupTaskTemplatesAPI(experimentGroupId: string) {
    const basePath = `/taskTemplates/experimentGroup/${experimentGroupId}`;
    return buildConventionalCrudAPI<ExperimentGroupTaskTemplate, SavedExperimentGroupTaskTemplate>(
        basePath
    );
}

export function buildDataCollectionTasksAPIForExperiment(experimentCode: string) {
    const basePath = `/measurements/dataCollectionTasks/${experimentCode}`;
    const baseCrudAPI = buildConventionalCrudAPI(basePath);

    function createNewDefaultTask(protocolCode: string) {
        const newDefaultDataCollectionTask = {
            experimentCode,
            protocolCode,
            type: "default",
            internal: false,
        };
        return baseCrudAPI.create(newDefaultDataCollectionTask);
    }

    return {
        ...baseCrudAPI,
        buildTasksFromTaskGroups: async () => {
            return post(`${basePath}/buildTasksFromTaskGroups`);
        },
        bulkSave: async (tasks: unknown[]) => {
            return post(`${basePath}/bulk`, tasks);
        },
        createNewDefaultTask,
    };
}

export const taskScheduleAPI = {
    fetchTaskScheduleForExperimentCode: async (experimentCode: string) => {
        return get(`/measurements/taskSchedule/${experimentCode}`);
    },
    fetchTaskSchedulesForExperimentCodes: async (experimentCodes: string[]) => {
        const queryString = experimentCodes.map((code) => `experimentCodes=${code}`).join("&");
        return get(`/measurements/taskSchedules?${queryString}`);
    },
};

export const measurementsAPI = {
    previewMigration: async (experimentCode: string) => {
        return get(`/measurements/migrate/${experimentCode}`);
    },
    fetchTaskScheduleForExperimentCode: async (experimentCode: string) => {
        return get(`/measurements/taskSchedule/${experimentCode}`);
    },
    setTaskStatusForTask: async (
        dataCollectionTaskId: string,
        status: unknown,
        timestamp: string
    ) => {
        return post(`/measurements/tasks/${dataCollectionTaskId}/fieldStatus`, {
            status,
            timestamp,
        });
    },
    fetchTaskScheduleWithCollectionInfoForExperimentCode: async (experimentCode: string) => {
        return get(`/measurements/taskScheduleCollectionInfo/${experimentCode}`);
    },
    fetchMyUserMeasurementSetForDataCollectionTaskId: async (dataCollectionTaskId: string) => {
        return get(`/measurements/myUserMeasurementSet/${dataCollectionTaskId}`);
    },
    fetchUserMeasurementSetsForDataCollectionTaskId: async (dataCollectionTaskId: string) => {
        return get(`/measurements/userMeasurementSets/${dataCollectionTaskId}`);
    },
    saveUserMeasurementSet: async (userMeasurementSet) => {
        // note: this calls the "merge" API; will replace save behavior.
        return post(`/measurements/userMeasurementSet/merge`, userMeasurementSet);
    },
    deleteUserMeasurementSet: async (userMeasurementSetId: string) => {
        return callDelete(`/measurements/userMeasurementSets/${userMeasurementSetId}`);
    },
    importTaskUserMeasurements: async ({
        experimentCode,
        dataCollectionTaskId,
        userId,
        collectionDateTime,
        file,
    }) => {
        const formData = new FormData();
        formData.set("file", file);
        formData.set("userId", userId);
        if (collectionDateTime) {
            formData.set("collectionDateTime", collectionDateTime);
        }
        if (dataCollectionTaskId) {
            formData.set("dataCollectionTaskId", dataCollectionTaskId);
        }
        return post(`/measurements/userMeasurementSets/import/${experimentCode}`, formData);
    },
    importAdHocUserMeasurements: async ({
        experimentCode,
        thingSchemaId,
        userId,
        collectionDateTime,
        file,
    }) => {
        const formData = new FormData();
        formData.set("file", file);
        formData.set("userId", userId);
        if (collectionDateTime) {
            formData.set("collectionDateTime", collectionDateTime);
        }
        formData.set("thingSchemaId", thingSchemaId);
        return post(`/measurements/userMeasurementSets/import/adhoc/${experimentCode}`, formData);
    },
    importSetupData: async (params: {
        experimentCode: string;
        thingSchemaId: string;
        file: Blob;
    }) => {
        const { experimentCode, thingSchemaId, file } = params;
        const formData = new FormData();
        formData.set("file", file);
        formData.set("thingSchemaId", thingSchemaId);
        return post(`/measurements/importSetupData/${experimentCode}`, formData);
    },
    downloadUserMeasurementsTemplate: async (dataCollectionTaskId: string, userId: string) => {
        return get(
            `/measurements/userMeasurementSets/template/${dataCollectionTaskId}/user/${userId}`,
            {
                responseType: "blob",
            }
        );
    },
    downloadMultiTaskUserMeasurementsTemplate: async (
        dataCollectionTaskIds: string[],
        userId: string
    ) => {
        const queryString = `dataCollectionTaskIds=${dataCollectionTaskIds.join(",")}`;
        return get(
            `/measurements/userMeasurementSets/multiTemplate/user/${userId}?${queryString}`,
            {
                responseType: "blob",
            }
        );
    },
    downloadAdHocUserMeasurementsTemplate: async (
        experimentCode: string,
        thingSchemaId: string
    ) => {
        return get(
            `/measurements/userMeasurementSets/adhocTemplate/${experimentCode}/${thingSchemaId}`,
            {
                responseType: "blob",
            }
        );
    },
    fetchPerTaskMeasurementSetsForDataCollectionTaskId: async (dataCollectionTaskId: string) => {
        return get(`/measurements/summaryPerTaskMeasurementSets/${dataCollectionTaskId}`);
    },
    fetchSummaryOneTimeMeasurementSets: async (experimentCode: string) => {
        return get(`/measurements/summaryOneTimeMeasurementSets/${experimentCode}`);
    },
    fetchSummaryTimeSeriesMeasurementSets: async (dataCollectionTaskId: string) => {
        return get(`/measurements/summaryTimeSeriesMeasurementSets/${dataCollectionTaskId}`);
    },
    fetchAggregatorProcessStatus: async () => {
        return get(`/measurements/aggregatorProcessStatus`);
    },
    startAggregatorProcess: async () => {
        return post(`/measurements/aggregateUserMeasurements`);
    },
    stopAggregatorProcess: async () => {
        return callDelete(`/measurements/aggregateUserMeasurements`);
    },
    fetchQCSummary: async (selectedSchemaConfigIds: string[]) => {
        const queryParam = selectedSchemaConfigIds.join(",");
        return get(`/measurements/qcSummary?schemaConfigIds=${queryParam}`);
    },
    updateQCStatus: async (dataCollectionTaskId: string, qcStatus) => {
        return put(`/measurements/qcStatus/${dataCollectionTaskId}`, qcStatus);
    },
};

export const backgroundTasksAPI = {
    fetchBackgroundTasks: async () => {
        return get(`/backgroundTasks`);
    },
    fetchBackgroundTaskById: async (id) => {
        return get(`/backgroundTasks/${id}`);
    },
    enqueueBackgroundTask: async (queuedTaskParams) => {
        const formData = buildBackgroundTaskPayload(queuedTaskParams);
        return post(`/backgroundTasks`, formData);
    },
    enqueueBackgroundTaskWithFileAttachment: async (queuedTaskParams, file) => {
        const formData = buildBackgroundTaskPayload(queuedTaskParams);
        formData.append("file", file);
        return post(`/backgroundTasks`, formData);
    },
};

function buildBackgroundTaskPayload(queuedTaskParams) {
    const formData = new FormData();
    const formJson = JSON.stringify(queuedTaskParams);
    const blob = new Blob([formJson], { type: "application/json" });
    formData.append("data", blob);
    return formData;
}

export const requestsAPI = {
    fetchRequestsByExperiment: async (experimentCode: string) => {
        return get(`/requests/getbyexp/${experimentCode}`);
    },
    fetchRequestCountByExperiment: async (experimentCode: string) => {
        return get(`/requests/count/${experimentCode}`);
    },
    fetchRequestProtocols: async () => {
        return get(`/requests/protocols`);
    },
    fetchPlateMap: async (requestCode: string) => {
        return get(`/requests/plateMap/${requestCode}`);
    },
};

export async function fetchConditionsByExperiment(id) {
    return get(`/experiment/${encodeURIComponent(id)}/conditions`);
}

export async function fetchPlateUploadHistory(experimentCode: string) {
    return get(`/experiment/plate/uploadHistory/${experimentCode}`);
}

export async function downloadRequestExcel(requestId: string) {
    return get(`/requests/${requestId}/download`, {
        responseType: "blob",
    });
}

export async function download384Excel(requestId: string, fileName: string) {
    return get(`/requests/${requestId}/download384/${fileName}`, {
        responseType: "blob",
    });
}

export async function downloadStandardsExcel(requestId: string, runId: string) {
    return get(`/runs/${requestId}/${runId}/download`, {
        responseType: "blob",
    });
}

export async function fetchRequests(filter: { protocolId: string; statusId: string }) {
    const { protocolId, statusId } = filter;

    return get("/requests", { params: { protocolId, statusId } });
}

export async function fetchRequestForEdit(requestCode: string) {
    return get(`/requests/${requestCode}/edit`);
}

export async function archiveExperiment(experimentCode: string) {
    return callDelete(`/experiment/${experimentCode}`);
}

/**
 * The goal is that these are the only things left in this file - move other calls to their own
 * API classes
 */

export async function callDelete<R>(path: string): Promise<R> {
    return axios.delete(path).then((response) => response.data as R);
}

export async function postFileUpload<P extends object, R>(
    path: string,
    payload: P,
    reqConfig?
): Promise<R> {
    const formData = toFormData(payload);
    return axios.post(path, formData, reqConfig).then((response) => response.data as R);
}

export async function post<P, R>(path: string, json?: P, reqConfig?): Promise<R> {
    return axios.post(path, json, reqConfig).then((response) => response.data as R);
}

export async function put<P, R>(path: string, json: P): Promise<R> {
    return axios.put(path, json).then((response) => response.data as R);
}

export async function get<T>(path: string, reqConfig?): Promise<T> {
    return axios.get(path, reqConfig).then((response) => response.data as T);
}
